/**
 * Datas, e o começo da semana.
 *
 * **A tabela abaixo é a autoridade — de propósito, e não `Intl`.**
 *
 * `Intl.Locale.prototype.getWeekInfo()` sabe responder isso, e ainda assim não é
 * usado aqui. O motivo: a resposta varia entre navegadores e versões. A mesma
 * pessoa veria uma semana começando no domingo no celular e na segunda no
 * desktop, e a aderência semanal que o aluno vê discordaria da que o
 * profissional vê — sobre exatamente os mesmos treinos.
 *
 * Numa tela de calendário, seguir o costume local é elegante. Numa métrica que
 * duas pessoas comparam entre si, concordar vale mais do que acertar o costume.
 * Usada por plano, atenção, desafio e métricas (SPEC §5.1).
 */

/** 0 = domingo, 1 = segunda. */
export type WeekStart = 0 | 1;

export const WEEK_START: Readonly<Record<string, WeekStart>> = {
  'pt-BR': 0, // domingo
  'en-US': 0, // Sunday
  'es-419': 1, // lunes — predominante na América Latina hispanofalante
};

const DEFAULT_WEEK_START: WeekStart = 0;

export function weekStartFor(locale: string): WeekStart {
  const base = locale.split('-')[0] ?? locale;
  return WEEK_START[locale] ?? WEEK_START[base] ?? DEFAULT_WEEK_START;
}

/**
 * Início da semana da data informada, à meia-noite local.
 *
 * Trabalha em horário local e não em UTC: "esta semana" é a semana de quem
 * está olhando. Para alguém em Manaus às 22h de sábado, a semana em UTC já
 * virou — e o treino de sábado apareceria na semana seguinte.
 */
export function startOfWeek(date: Date, locale: string): Date {
  const start = weekStartFor(locale);
  const result = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const shift = (result.getDay() - start + 7) % 7;
  result.setDate(result.getDate() - shift);
  return result;
}

export function endOfWeek(date: Date, locale: string): Date {
  const result = startOfWeek(date, locale);
  result.setDate(result.getDate() + 6);
  return result;
}

export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** Os sete dias da semana, na ordem do locale — para cabeçalho de calendário. */
export function weekdayLabels(locale: string, format: 'short' | 'narrow' = 'short'): string[] {
  const formatter = new Intl.DateTimeFormat(locale, { weekday: format });
  const start = weekStartFor(locale);
  // 2024-01-07 é um domingo; serve só de âncora para gerar os nomes.
  const anchor = new Date(2024, 0, 7);
  return Array.from({ length: 7 }, (_, index) => {
    const day = new Date(anchor);
    day.setDate(anchor.getDate() + ((start + index) % 7));
    return formatter.format(day);
  });
}

export function formatDate(date: Date, locale: string): string {
  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long' }).format(date);
}

export function formatWeekday(date: Date, locale: string): string {
  return new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(date);
}

/**
 * "há 2 dias", "em 3 dias".
 *
 * Compara dias de calendário, não intervalos de 24 horas: às 23h de segunda,
 * um treino das 1h da manhã é "hoje", e não "há 22 horas".
 */
export function formatRelativeDay(date: Date, locale: string, now = new Date()): string {
  const formatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const days = Math.round((target.getTime() - today.getTime()) / 86_400_000);
  return formatter.format(days, 'day');
}
